import type { MetadataKind, MetadataObject } from '@/types/metadata'

export const METADATA_KIND_LABELS: Record<MetadataKind, string> = {
  catalog: 'Справочники',
  document: 'Документы',
  register: 'Регистры',
  constant: 'Константы',
}

export const METADATA_OBJECTS: MetadataObject[] = [
  // ── Справочники ─────────────────────────────────────────────
  {
    id: 'nomenclature',
    kind: 'catalog',
    name: 'Номенклатура',
    description: 'GTIN, бренд, шаблоны этикеток и доп. поля товара',
    dbTable: 'nomenclature',
    module: 'internal/nomenclature',
    statsKey: 'nomenclature_count',
    uiStatus: 'ready',
    uiRoute: '/admin/nomenclature',
  },
  {
    id: 'product_groups',
    kind: 'catalog',
    name: 'Товарные группы',
    description: 'Группы маркировки ЭЗ и структура кода',
    dbTable: 'product_groups',
    module: 'internal/codestructure',
    uiStatus: 'ready',
    uiRoute: '/admin/settings/code-structures',
  },
  {
    id: 'ka_templates',
    kind: 'catalog',
    name: 'Шаблоны КА',
    description: 'Структура кодов агрегации для коробок и паллет',
    dbTable: 'ka_templates',
    module: 'internal/katemplate',
    uiStatus: 'ready',
    uiRoute: '/admin/ka-templates',
  },
  {
    id: 'label_templates',
    kind: 'catalog',
    name: 'Шаблоны этикеток',
    description: 'Макеты КМ / КА для печати и предпросмотра',
    dbTable: 'label_templates',
    module: 'internal/labeltemplate',
    uiStatus: 'ready',
    uiRoute: '/admin/settings/label-templates',
  },
  {
    id: 'gs1_ai_catalog',
    kind: 'catalog',
    name: 'Каталог GS1 AI',
    description: 'Справочник идентификаторов применения (read-only)',
    dbTable: 'gs1_ai_catalog',
    module: 'internal/gs1',
    uiStatus: 'schema',
  },

  // ── Документы ───────────────────────────────────────────────
  {
    id: 'tasks',
    kind: 'document',
    name: 'Задание',
    description: 'Производственное задание линии: КМ, КА, scan, печать',
    dbTable: 'tasks',
    module: 'internal/tasks',
    statsKey: 'tasks_total',
    uiStatus: 'ready',
    uiRoute: '/admin/tasks',
    featureFlag: 'enable_tasks',
  },
  {
    id: 'shipments',
    kind: 'document',
    name: 'Отгрузка',
    description: 'Документ отгрузки маркированной продукции',
    dbTable: 'shipments',
    module: 'internal/shipments',
    statsKey: 'shipments_total',
    uiStatus: 'ready',
    uiRoute: '/admin/shipments',
    featureFlag: 'enable_shipments',
  },
  {
    id: 'ka_generation_documents',
    kind: 'document',
    name: 'Генерация КА',
    description: 'Документ генерации или импорта кодов агрегации',
    dbTable: 'ka_generation_documents',
    module: 'internal/kageneration',
    uiStatus: 'ready',
    uiRoute: '/admin/ka-generation',
    featureFlag: 'enable_ka_generation',
  },

  // ── Регистры ────────────────────────────────────────────────
  {
    id: 'marking_codes',
    kind: 'register',
    name: 'Пул КМ',
    description: 'Глобальный реестр кодов маркировки',
    dbTable: 'marking_codes',
    module: 'internal/storage',
    uiStatus: 'schema',
  },
  {
    id: 'aggregation_codes',
    kind: 'register',
    name: 'Пул КА',
    description: 'Глобальный реестр кодов агрегации',
    dbTable: 'aggregation_codes',
    module: 'internal/storage',
    uiStatus: 'schema',
  },
  {
    id: 'aggregation_code_contents',
    kind: 'register',
    name: 'Состав коробки',
    description: 'Привязка КМ к коду коробки (КА)',
    dbTable: 'aggregation_code_contents',
    module: 'internal/storage',
    uiStatus: 'schema',
  },
  {
    id: 'aggregation_code_nesting',
    kind: 'register',
    name: 'Вложенность КА',
    description: 'Коробка внутри паллеты',
    dbTable: 'aggregation_code_nesting',
    module: 'internal/storage',
    uiStatus: 'schema',
  },
  {
    id: 'task_codes',
    kind: 'register',
    name: 'КМ в задании',
    description: 'Коды маркировки, выделенные под задание',
    dbTable: 'task_codes',
    module: 'internal/tasks',
    uiStatus: 'schema',
  },
  {
    id: 'ka_generation_document_codes',
    kind: 'register',
    name: 'КА в документе генерации',
    description: 'Строки табличной части документа генерации КА',
    dbTable: 'ka_generation_document_codes',
    module: 'internal/kageneration',
    uiStatus: 'schema',
  },
  {
    id: 'task_box_scan_state',
    kind: 'register',
    name: 'Состояние коробки (scan)',
    description: 'Открыта / закрыта / отклонена коробка на линии',
    dbTable: 'task_box_scan_state',
    module: 'internal/scan',
    uiStatus: 'schema',
  },
  {
    id: 'scan_events',
    kind: 'register',
    name: 'Журнал сканов',
    description: 'Аудит результатов сканирования КМ',
    dbTable: 'scan_events',
    module: 'internal/scan',
    uiStatus: 'schema',
  },
  {
    id: 'label_prints',
    kind: 'register',
    name: 'Факты печати',
    description: 'Зафиксированные печати КМ и КА по заданию',
    dbTable: 'label_prints',
    module: 'internal/print',
    statsKey: 'labels_total',
    uiStatus: 'schema',
  },
  {
    id: 'shipment_additions',
    kind: 'register',
    name: 'Добавления в отгрузку',
    description: 'Корневые строки сканирования в документ отгрузки',
    dbTable: 'shipment_additions',
    module: 'internal/shipments',
    uiStatus: 'schema',
  },
  {
    id: 'shipment_items',
    kind: 'register',
    name: 'Состав отгрузки',
    description: 'Развёрнутый реестр КМ / коробок / паллет в отгрузке',
    dbTable: 'shipment_items',
    module: 'internal/shipments',
    uiStatus: 'schema',
  },

  // ── Константы ───────────────────────────────────────────────
  {
    id: 'general_settings',
    kind: 'constant',
    name: 'Общие настройки',
    description: 'Режим печати, дубликаты скана, производительность',
    dbTable: 'app_settings.general_settings',
    module: 'internal/settings',
    uiStatus: 'ready',
    uiRoute: '/admin/settings',
  },
  {
    id: 'feature_flags',
    kind: 'constant',
    name: 'Feature flags',
    description: 'Включение модулей заданий, отгрузок, генерации КА',
    dbTable: 'app_settings.feature_flags',
    module: 'internal/settings',
    uiStatus: 'ready',
    uiRoute: '/admin/settings',
  },
  {
    id: 'sscc_counters',
    kind: 'constant',
    name: 'SSCC-счётчики',
    description: 'Серийные номера для генерации SSCC',
    dbTable: 'app_settings.sscc_counters',
    module: 'internal/settings',
    uiStatus: 'ready',
    uiRoute: '/admin/settings',
  },
  {
    id: 'gs1_company_prefix',
    kind: 'constant',
    name: 'GS1 company prefix',
    description: 'Префикс компании для SSCC и КА',
    dbTable: 'app_settings.gs1_company_prefix',
    module: 'internal/settings',
    uiStatus: 'ready',
    uiRoute: '/admin/settings',
  },
  {
    id: 'database_settings',
    kind: 'constant',
    name: 'База и Redis',
    description: 'Подключение PostgreSQL / Redis',
    dbTable: 'app_settings.database_settings',
    module: 'internal/settings',
    uiStatus: 'ready',
    uiRoute: '/admin/infrastructure',
  },
  {
    id: 'sidebar_config',
    kind: 'constant',
    name: 'Навигация sidebar',
    description: 'Видимость пунктов меню admin / worker',
    dbTable: 'app_settings.sidebar',
    module: 'internal/sidebar',
    uiStatus: 'ready',
    uiRoute: '/admin/sidebar-settings',
  },
]

export function metadataByKind(kind: MetadataKind): MetadataObject[] {
  return METADATA_OBJECTS.filter((o) => o.kind === kind)
}
